import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { UserPlus, CalendarCheck, UserMinus, Search, Pencil } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/pickers";
import { formatTime12 } from "@/lib/format";

const ALL = "__all__";
const CLEAR = "__clear__";
const RULE_TEXT = {
  min_rest: (v) => `only ${v ?? "?"} hours of rest between shifts`,
  max_consec_days: (v) => `${v ?? "too many"} working days in a row`,
  max_consec_nights: (v) => `${v ?? "too many"} night shifts in a row`,
};

function FilterSelect({ value, onChange, options, label, testid }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="h-9 w-[170px]" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {label.toLowerCase()}s</SelectItem>
        {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function useFilters() {
  const [f, setF] = useState({ search: "", department_id: "", designation_id: "", location_id: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  return [f, set];
}

function FilterBar({ f, set, masters, prefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[180px] flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input className="h-9 pl-9" placeholder="Search name or code" value={f.search} onChange={(e) => set("search")(e.target.value)} data-testid={`${prefix}-search`} />
      </div>
      <FilterSelect value={f.department_id} onChange={set("department_id")} options={masters.departments} label="Department" testid={`${prefix}-filter-department`} />
      <FilterSelect value={f.designation_id} onChange={set("designation_id")} options={masters.designations} label="Designation" testid={`${prefix}-filter-designation`} />
      <FilterSelect value={f.location_id} onChange={set("location_id")} options={masters.locations} label="Location" testid={`${prefix}-filter-location`} />
    </div>
  );
}

function ImportDialog({ open, onClose, planId, existing, masters, onDone }) {
  const { actingOrgId } = useAuth();
  const [f, set] = useFilters();
  const [sel, setSel] = useState([]);
  const { data: emps = [], isLoading } = useQuery({
    queryKey: ["import-emps", actingOrgId, f], enabled: open,
    queryFn: async () => (await api.get("/org/employees", { params: f })).data,
  });
  const list = emps.filter((e) => !existing.has(e.id));
  const add = async () => {
    try {
      await api.post(`/roster/plans/${planId}/employees`, { add: sel });
      toast.success(`${sel.length} employee(s) added to the roster`); setSel([]); onDone(); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add employees from the employee master</DialogTitle>
          <DialogDescription>Filter by department, designation or location, then pick who joins this month's roster.</DialogDescription>
        </DialogHeader>
        <FilterBar f={f} set={set} masters={masters} prefix="import" />
        <div className="max-h-80 overflow-auto rounded border border-slate-100">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs text-slate-500"><tr>
              <th className="w-8 p-2"><Checkbox checked={list.length > 0 && sel.length === list.length} onCheckedChange={(v) => setSel(v ? list.map((e) => e.id) : [])} data-testid="import-select-all" /></th>
              <th className="p-2">Name</th><th className="p-2">Department</th><th className="p-2">Designation</th><th className="p-2">Location</th>
            </tr></thead>
            <tbody>
              {list.map((e) => (
                <tr key={e.id} className="border-t border-slate-100" data-testid={`import-row-${e.id}`}>
                  <td className="p-2"><Checkbox checked={sel.includes(e.id)} onCheckedChange={(v) => setSel(v ? [...sel, e.id] : sel.filter((x) => x !== e.id))} data-testid={`import-select-${e.id}`} /></td>
                  <td className="p-2">{e.name}<span className="ml-1 text-xs text-slate-400">{e.employee_code}</span></td>
                  <td className="p-2">{e.department_name || "-"}</td><td className="p-2">{e.designation_name || "-"}</td><td className="p-2">{e.location_name || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isLoading && !list.length && <p className="p-6 text-center text-sm text-slate-400">No more employees match these filters.</p>}
        </div>
        <DialogFooter>
          <Button disabled={!sel.length} onClick={add} data-testid="import-submit">Add {sel.length || ""} to roster</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignBar({ planId, dates, shifts, selected, onDone }) {
  const [shift, setShift] = useState("");
  const [from, setFrom] = useState(dates[0]);
  const [to, setTo] = useState(dates[dates.length - 1]);
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/roster/plans/${planId}/assign`, { employee_ids: selected, shift_code: shift === CLEAR ? "" : shift, from_date: from, to_date: to });
      toast.success(data.skipped ? `Saved ${data.updated} day(s). Skipped ${data.skipped} week-off, leave or holiday day(s).` : `Saved for ${selected.length} employee(s)`); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-wrap items-end gap-3 p-4" data-testid="assign-bar">
      <div><p className="text-xs text-slate-500">Shift</p>
        <Select value={shift} onValueChange={setShift}>
          <SelectTrigger className="mt-1 h-9 w-48" data-testid="assign-shift"><SelectValue placeholder="Pick a shift" /></SelectTrigger>
          <SelectContent>
            {shifts.map((s) => <SelectItem key={s.id} value={s.code}>{s.code} · {s.name} ({formatTime12(s.start)} – {formatTime12(s.end)})</SelectItem>)}
            <SelectItem value="WO">WO · Weekly off</SelectItem>
            <SelectItem value="L">L · Leave</SelectItem>
            <SelectItem value={CLEAR}>Clear cells</SelectItem>
          </SelectContent>
        </Select></div>
      <div className="w-40"><p className="text-xs text-slate-500">From</p><div className="mt-1"><DatePicker mode="iso" min={dates[0]} max={dates[dates.length - 1]} value={from} onChange={setFrom} testid="assign-from" /></div></div>
      <div className="w-40"><p className="text-xs text-slate-500">To</p><div className="mt-1"><DatePicker mode="iso" min={from || dates[0]} max={dates[dates.length - 1]} value={to} onChange={setTo} testid="assign-to" /></div></div>
      <Button disabled={!shift || !selected.length || busy} onClick={assign} data-testid="assign-submit">
        <CalendarCheck className="mr-1 h-4 w-4" /> Assign to {selected.length} selected
      </Button>
      {!shifts.length && <p className="text-xs text-amber-600">No shifts exist for this location yet. An org admin has to create them first.</p>}
      <p className="w-full text-xs text-slate-500" data-testid="assign-skip-hint">Shifts are never placed on a person's week off, leave or holiday; those days are skipped. To change a single day, click it (or its pencil) and pick a shift, WO or leave.</p>
    </Card>
  );
}

function Palette({ shifts, value, onChange, colorFor }) {
  const items = [["", "Edit mode"], ...shifts.map((s) => [s.code, s.name]), ["WO", "Week off"], [CLEAR, "Clear"]];
  return (
    <Card className="sticky top-14 z-30 flex flex-wrap items-center gap-2 p-3 lg:top-2" data-testid="paint-palette">
      <span className="text-sm font-semibold text-slate-800">Paint:</span>
      {items.map(([c, n]) => (
        <button key={c || "none"} onClick={() => onChange(c)} data-testid={`paint-${c === CLEAR ? "clear" : c || "none"}`} title={n}
          className={`flex h-9 min-w-[44px] items-center justify-center gap-1 rounded-md border-2 px-2 text-sm font-bold text-slate-900 ${value === c ? "border-indigo-600 ring-2 ring-indigo-200" : "border-transparent"}`}
          style={{ background: c === CLEAR || !c ? "#f1f5f9" : colorFor(c) }}>{c === CLEAR ? "Clear" : c || <Pencil className="h-4 w-4" />}</button>
      ))}
      <span className="hidden w-full text-xs text-slate-600 sm:block">Click a day, or press and drag along a row. Click or drag a date header to fill that day for everyone shown. Shifts skip week offs, leave and holidays; WO and Clear work on any day.</span>
    </Card>
  );
}

function Grid({ board, rows, selected, setSelected, colorFor, locked, onPaint, brush, onEdit, startOf }) {
  const [drag, setDrag] = useState(null);
  useEffect(() => {
    if (!drag) return undefined;
    const up = () => {
      const [a, b] = [drag.from, drag.to].sort();
      onPaint(drag.emp === "*" ? rows.map((e) => e.id) : [drag.emp], a, b);
      setDrag(null);
    };
    window.addEventListener("pointerup", up);
    return () => window.removeEventListener("pointerup", up);
  }, [drag, rows, onPaint]);
  const inDrag = (emp, d) => drag && (drag.emp === "*" || drag.emp === emp) && d >= [drag.from, drag.to].sort()[0] && d <= [drag.from, drag.to].sort()[1];
  const ptr = useRef("mouse");
  const start = (emp, d) => (ev) => { ptr.current = ev.pointerType; if (onPaint && brush && ev.pointerType === "mouse") { ev.preventDefault(); setDrag({ emp, from: d, to: d }); } };
  const tap = (emp, d) => () => {
    if (onPaint && brush) { if (ptr.current !== "mouse") onPaint(emp === "*" ? rows.map((e) => e.id) : [emp], d, d); }
    else if (!locked && emp !== "*") onEdit(emp, d);
  };
  const over = (emp, d) => () => { if (drag && drag.emp === emp) setDrag({ ...drag, to: d }); };
  const allSel = rows.length > 0 && rows.every((e) => selected.includes(e.id));
  const toggle = (id, v) => setSelected(v ? [...selected, id] : selected.filter((x) => x !== id));
  const wk = (d) => new Date(`${d}T00:00:00`).getDay();
  const DAY = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  return (
    <Card className="overflow-auto p-0" style={{ maxHeight: 620 }}>
      <table className="border-separate border-spacing-0 text-sm" data-testid="roster-grid">
        <thead className="sticky top-0 z-10 bg-white"><tr>
          <th className="sticky left-0 z-20 min-w-[140px] sm:min-w-[220px] border-b-2 border-slate-200 bg-slate-100 px-3 py-2 text-left font-semibold text-slate-900">
            <div className="flex items-center gap-2">{!locked && <Checkbox checked={allSel} onCheckedChange={(v) => { const shown = rows.map((e) => e.id); setSelected(v ? [...new Set([...selected, ...shown])] : selected.filter((x) => !shown.includes(x))); }} data-testid="grid-select-all" />} Employee</div>
          </th>
          {board.dates.map((d) => (
            <th key={d} onPointerDown={start("*", d)} onPointerEnter={over("*", d)} onClick={tap("*", d)} data-testid={`grid-head-${d}`}
              className={`min-w-[52px] select-none border-b-2 border-slate-200 px-1 py-1.5 text-center font-semibold ${onPaint ? "cursor-pointer" : ""} ${inDrag("*", d) ? "bg-indigo-200 text-indigo-900" : [0, 6].includes(wk(d)) ? "bg-amber-50 text-amber-900" : "bg-slate-50 text-slate-800"}`}>
              <div className="text-base leading-tight">{+d.slice(8)}</div><div className="text-xs font-medium">{DAY[wk(d)]}</div>
            </th>
          ))}
        </tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} data-testid={`grid-row-${e.id}`} className="hover:bg-slate-50">
              <td className="sticky left-0 z-[5] border-b border-slate-100 bg-white px-3 py-1.5">
                <div className="flex items-center gap-2 whitespace-nowrap">
                  {!locked && <Checkbox checked={selected.includes(e.id)} onCheckedChange={(v) => toggle(e.id, v)} data-testid={`grid-select-${e.id}`} />}
                  <span className="font-semibold text-slate-900">{e.name}</span><span className="hidden text-xs text-slate-500 sm:inline">{e.employee_code}</span>
                </div>
              </td>
              {board.dates.map((d) => {
                const c = board.roster[e.key]?.[d] || "";
                const t = startOf(c);
                return (
                  <td key={d} onPointerDown={start(e.id, d)} onPointerEnter={over(e.id, d)} onClick={tap(e.id, d)} data-testid={`cell-${e.id}-${d}`}
                    className={`group relative select-none border-b border-slate-100 p-0.5 text-center ${!locked ? "cursor-pointer" : ""} ${inDrag(e.id, d) ? "bg-indigo-100" : ""}`} title={t ? `${c} starts ${t}` : c}>
                    {c ? (
                      <div className="flex h-11 flex-col items-center justify-center rounded-md leading-tight text-slate-900" style={{ background: colorFor(c) }}>
                        <span className="text-sm font-bold">{c}</span>
                        {t && <span className="whitespace-nowrap text-[10px] font-semibold text-slate-700" data-testid={`cell-time-${e.id}-${d}`}>{t}</span>}
                      </div>
                    ) : <div className={`h-11 rounded-md ${onPaint ? "border border-dashed border-slate-200" : ""}`} />}
                    {!locked && (
                      <button type="button" aria-label="Edit this day" data-testid={`cell-edit-${e.id}-${d}`}
                        onPointerDown={(ev) => ev.stopPropagation()} onClick={(ev) => { ev.stopPropagation(); onEdit(e.id, d); }}
                        className="absolute right-0 top-0 rounded bg-white/90 p-0.5 text-indigo-700 shadow sm:hidden sm:group-hover:block">
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <p className="p-8 text-center text-base text-slate-500">No employees on this roster yet.</p>}
    </Card>
  );
}

function DayEditor({ edit, board, shifts, colorFor, onClose, onDone }) {
  const { isManagerOnly } = useAuth();
  const cur = board.roster[edit.emp.key]?.[edit.d] || "";
  const set = async (code) => {
    try {
      await api.post(`/roster/plans/${board.plan.id}/assign`, { employee_ids: [edit.emp.id], shift_code: code, from_date: edit.d, to_date: edit.d, force: true });
      toast.success(code ? `${edit.emp.name}: ${code} on ${edit.d}` : "Day cleared"); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const opts = [...shifts.map((s) => [s.code, `${s.name} · ${formatTime12(s.start)} – ${formatTime12(s.end)}`]), ["WO", "Week off"], ["L", "Leave"], ...(isManagerOnly ? [] : [["H", "Holiday"]])];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="day-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4" /> {edit.emp.name}</DialogTitle>
          <p className="text-sm text-slate-600">{new Date(`${edit.d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} · now: <b>{cur || "empty"}</b></p>
        </DialogHeader>
        <div className="grid gap-2">
          {opts.map(([c, label]) => (
            <button key={c} onClick={() => set(c)} data-testid={`day-set-${c}`}
              className={`flex items-center gap-3 rounded-lg border-2 px-3 py-2 text-left text-sm ${cur === c ? "border-indigo-600" : "border-slate-200 hover:border-slate-300"}`}>
              <span className="flex h-8 w-12 items-center justify-center rounded-md font-bold text-slate-900" style={{ background: colorFor(c) }}>{c}</span>
              <span className="text-slate-800">{label}</span>
            </button>
          ))}
          <Button variant="outline" onClick={() => set("")} data-testid="day-set-clear">Clear this day</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UnassignedCounts({ board }) {
  const counts = useMemo(() => {
    let none = 0;
    let gaps = 0;
    board.employees.forEach((e) => {
      const row = board.roster[e.key] || {};
      const filled = board.dates.filter((d) => row[d]).length;
      if (!filled) none += 1;
      if (filled < board.dates.length) gaps += 1;
    });
    return { none, gaps };
  }, [board]);
  if (!board.employees.length) return null;
  return (
    <div className="flex flex-wrap gap-2 text-sm" data-testid="unassigned-counts">
      <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-800" data-testid="count-selected">{board.employees.length} selected</span>
      <span className={`rounded-full px-3 py-1 font-semibold ${counts.none ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`} data-testid="count-nothing-assigned">{counts.none} with nothing assigned</span>
      <span className={`rounded-full px-3 py-1 font-semibold ${counts.gaps ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"}`} data-testid="count-empty-days">{counts.gaps} with empty days</span>
    </div>
  );
}

export function RosterBoard({ board, shifts, masters, onChanged, colours = {}, locked = false, paint = false }) {
  const [f, set] = useFilters();
  const [selected, setSelected] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [brush, setBrush] = useState("");
  const [edit, setEdit] = useState(null);
  const startOf = useCallback((c) => { const s = shifts.find((x) => x.code === c); return s ? formatTime12(s.start).replace(":00", "") : ""; }, [shifts]);
  const onPaint = useCallback(async (ids, from, to) => {
    if (!brush) { toast.error("Pick a shift, WO or Clear from the palette first"); return; }
    try {
      const { data } = await api.post(`/roster/plans/${board.plan.id}/assign`, { employee_ids: ids, shift_code: brush === CLEAR ? "" : brush, from_date: from, to_date: to });
      if (data.skipped) toast.message(`${data.skipped} week-off, leave or holiday day(s) skipped`);
      onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [brush, board.plan.id, onChanged]);
  const rows = useMemo(() => board.employees.filter((e) =>
    (!f.department_id || e.department_id === f.department_id) && (!f.designation_id || e.designation_id === f.designation_id) &&
    (!f.location_id || e.location_id === f.location_id) &&
    (!f.search || `${e.name} ${e.employee_code || ""}`.toLowerCase().includes(f.search.toLowerCase()))), [board.employees, f]);
  const sel = selected.filter((id) => board.employees.some((e) => e.id === id));
  const hiddenSel = sel.filter((id) => !rows.some((e) => e.id === id)).length;
  const colorFor = (c) => colours[c] || "#e0e7ff";
  const removeSel = async () => {
    try { await api.post(`/roster/plans/${board.plan.id}/employees`, { remove: sel }); toast.success("Removed from roster"); setSelected([]); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <UnassignedCounts board={board} />
      {!locked && <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-base font-bold text-slate-900">Edit the roster ({board.employees.length} employees)</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={!sel.length} onClick={removeSel} data-testid="remove-from-roster"><UserMinus className="mr-1 h-4 w-4" /> Remove selected</Button>
            <Button size="sm" onClick={() => setImportOpen(true)} data-testid="open-import"><UserPlus className="mr-1 h-4 w-4" /> Add employees</Button>
          </div>
        </div>
        <FilterBar f={f} set={set} masters={masters} prefix="board" />
        {rows.length !== board.employees.length && <p className="text-sm text-slate-600" data-testid="board-showing">Showing {rows.length} of {board.employees.length}. Ticked people stay ticked when you search or change the filter; everyone stays on the roster.</p>}
        {!!sel.length && (
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="board-selected-summary">
            <span className="rounded-full bg-indigo-100 px-3 py-1 font-semibold text-indigo-800">{sel.length} ticked{hiddenSel ? ` (${hiddenSel} not shown by this filter)` : ""}</span>
            <Button size="sm" variant="ghost" onClick={() => setSelected([])} data-testid="board-clear-selection">Clear selection</Button>
          </div>
        )}
      </Card>}
      {!locked && <AssignBar key={board.plan.id} planId={board.plan.id} dates={board.dates} shifts={shifts} selected={sel} onDone={onChanged} />}
      {paint && <Palette shifts={shifts} value={brush} onChange={setBrush} colorFor={colorFor} />}
      <Grid board={board} rows={rows} selected={selected} setSelected={setSelected} colorFor={colorFor} locked={locked} onPaint={paint ? onPaint : null}
        brush={brush} startOf={startOf} onEdit={(emp, d) => setEdit({ emp: board.employees.find((x) => x.id === emp), d })} />
      {edit && <DayEditor edit={edit} board={board} shifts={shifts} colorFor={colorFor} onClose={() => setEdit(null)} onDone={() => { setEdit(null); onChanged(); }} />}
      {board.violations?.length > 0 && (
        <Card className="p-4" data-testid="violations-panel">
          <p className="text-base font-bold text-slate-900">Rule warnings</p>
          {board.violations.slice(0, 40).map((v, i) => <p key={i} className="mt-1 text-sm text-rose-700">{board.employees.find((e) => e.key === v.code)?.name || v.code} · {v.date}: {RULE_TEXT[v.rule] ? RULE_TEXT[v.rule](v.value) : v.rule}</p>)}
        </Card>
      )}
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} planId={board.plan.id} masters={masters}
        existing={new Set(board.employees.map((e) => e.id))} onDone={onChanged} />
    </div>
  );
}
